/**
 * aiErrorMessage — classify errors from /rewrite, /polish, /translate
 * and the rest of the Claude-backed routes into pilot-friendly text.
 *
 * Wave-4 audit fix (#9). The previous code surfaced
 * `error.message` raw, which often read like "Claude returned
 * invalid JSON after retry: …" or "fetch failed". Pilot users
 * couldn't tell whether they hit a rate limit, typed a bad
 * instruction, or had a network blip — so they couldn't decide
 * whether to retry, edit the input, or escalate.
 *
 * The classifier maps error.status (and a few common .message
 * patterns) onto four buckets:
 *
 *   - rateLimit  — 429 from the daily $X spend cap
 *   - validation — 400 (bad input shape) or 422 (Claude failed
 *                  to produce a valid response after retry)
 *   - network    — fetch threw before reaching the server
 *   - unknown    — fall-through; surface the raw message
 *
 * Each bucket has a short user-facing sentence + an optional
 * "advice" line that tells the user what to do next. Callers
 * render these in their existing error surfaces (the bubble
 * footer, the AiEnhancements error card, etc.).
 */
import { ApiError } from './apiClient.ts';

export type AiErrorKind =
  | 'rateLimit'
  | 'validation'
  | 'network'
  | 'unknown';

export interface ClassifiedAiError {
  kind: AiErrorKind;
  /** Short, polite headline for the error surface. */
  title: string;
  /** Optional second line — what the user can try next. */
  advice?: string;
  /** Raw upstream message, for the developer console / a
   *  collapsed "Details" disclosure. Never null — falls back
   *  to a generic string when the upstream gave us nothing. */
  raw: string;
}

export function classifyAiError(err: unknown): ClassifiedAiError {
  if (err instanceof ApiError) {
    const raw = err.message || `HTTP ${err.status}`;
    if (err.status === 429) {
      return {
        kind: 'rateLimit',
        title: 'Daily AI usage limit reached.',
        advice:
          'Reset is at midnight Eastern. Ask an admin to raise your limit if you need more headroom today.',
        raw,
      };
    }
    if (err.status === 422) {
      return {
        kind: 'validation',
        title: "Claude couldn't generate a valid response.",
        advice:
          'Try a different instruction, or shorten the selection so there is less to interpret.',
        raw,
      };
    }
    if (err.status === 400) {
      return {
        kind: 'validation',
        title: 'The request was rejected.',
        advice: raw.length < 200 ? raw : 'Check your input and try again.',
        raw,
      };
    }
    if (err.status === 403) {
      return {
        kind: 'validation',
        title: "You don't have permission for this action.",
        advice: 'Reach out to an admin if this should be allowed.',
        raw,
      };
    }
    if (err.status >= 500) {
      return {
        kind: 'unknown',
        title: 'The server hit an unexpected error.',
        advice: 'Try again in a moment. If it keeps happening, let an admin know.',
        raw,
      };
    }
    return {
      kind: 'unknown',
      title: 'Something went wrong.',
      advice: raw.length < 200 ? raw : undefined,
      raw,
    };
  }

  // TypeError / network failures (CORS, offline, DNS) come through
  // as plain Error with messages like "Failed to fetch" or
  // "NetworkError when attempting to fetch resource".
  if (err instanceof Error) {
    const msg = err.message ?? '';
    if (
      /fetch|network|cors|offline|connection/i.test(msg) &&
      !/anthropic|claude/i.test(msg)
    ) {
      return {
        kind: 'network',
        title: 'Connection issue.',
        advice: 'Check your network, then try again.',
        raw: msg,
      };
    }
    return {
      kind: 'unknown',
      title: 'Something went wrong.',
      advice: msg.length < 200 ? msg : undefined,
      raw: msg,
    };
  }

  return {
    kind: 'unknown',
    title: 'Something went wrong.',
    raw: 'Unknown error',
  };
}
